import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // Minimal base - just essentials for a button
  'inline-flex items-center justify-center cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-white hover:bg-destructive/90',
        outline: 'border bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // Custom variants for Cellfie - complete styles
        brand:
          'bg-[linear-gradient(90deg,_#F1656A_-66.86%,_#ED2891_47.89%,_#42419A_167.14%)] text-white hover:opacity-90 active:scale-95',
        black:
          'inline-flex items-center justify-center bg-[#171717] text-white hover:bg-[#0f0f0f] active:scale-95',
        white:
          'inline-flex items-center justify-center bg-white text-black hover:bg-white/90 border border-gray-200',
        // Unstyled - no default styles at all
        unstyled: '',
      },
      size: {
        default: 'h-9 px-4 py-2 rounded-md text-sm',
        sm: 'h-8 px-3 rounded-md text-sm',
        lg: 'h-10 px-6 rounded-md text-base',
        xl: 'h-12 px-8 rounded-md text-base',
        icon: 'h-9 w-9 rounded-md',
        // No size constraints
        none: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  type = 'button',
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      type={asChild ? undefined : type}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
